/**
 * Pre-initialize WebSocket connection to obtain / refresh a VDSS sessionId.
 *
 * - If there is no global VDSS session (or forceNew), sends session_id "aaaa"
 *   and stores whatever the server returns as the global session.
 * - Topic change should always use forceNew: true (always "aaaa").
 * - Editor view should pass the existing gVdssSessionId when available.
 *
 * @param {Object} topic - The selected topic object with connectUrl
 * @param {string} sessionId - The login session ID
 * @param {Function} onSessionReceived - Callback when sessionId is received from server
 * @param {Object} [options]
 * @param {string|null} [options.vdssSessionId] - Existing global VDSS session (Editor)
 * @param {boolean} [options.forceNew=false] - Always start with "aaaa" (Change Topic)
 * @returns {Promise} Resolves when sessionId is received or rejects on error
 */
export const wsPreInit = (topic, sessionId, onSessionReceived, options = {}) => {
  const { vdssSessionId = null, forceNew = false } = options
  const initSessionId =
    forceNew || !vdssSessionId || vdssSessionId === "aaaa" ? "aaaa" : vdssSessionId

  return new Promise((resolve, reject) => {
    if (!topic || !topic.connectUrl) {
      console.error("wsPreInit: Invalid topic or connectUrl")
      reject(new Error("Invalid topic or connectUrl"))
      return
    }

    let sessionReceived = false
    let completed = false
    const ws = new WebSocket(topic.connectUrl)
    console.log("wsPreInit connecting to:", topic.connectUrl, "session_id:", initSessionId)

    ws.onopen = () => {
      console.log("wsPreInit connected")

      const init = {
        config: {
          sample_rate: "16000",
          key: "2Njw!M2zApeAtdA6dG9kvBpzjT*rwqDbE2zzAs%P5Zuc?xzbpj!ZNnNA#u!gHM?%H4447TvPUgNtvGM?hgWe$qq#P2",
          session_id: initSessionId,
          loginSessionID: sessionId,
          topicID: topic.topic_id,
          version: 5,
          timeoutFactor: 100,
          platform: "Portal",
          clientCodeVersion: "1",
        }
      }

      ws.send(JSON.stringify(init))
      console.log("wsPreInit sent init with session_id:", initSessionId)
    }

    ws.onerror = (error) => {
      console.error("wsPreInit WebSocket error:", error)

      if (!completed) {
        completed = true
        reject(error)
      }

      ws.close()
    }

    ws.onclose = (event) => {
      console.log("wsPreInit WebSocket connection closed", event)

      if (!completed && !sessionReceived) {
        completed = true
        reject(new Error("WebSocket closed before sessionID was received"))
      }
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        const sessionID = data?.results?.sessionID

        if (sessionID) {
          console.log("wsPreInit Received sessionID:", sessionID)

          sessionReceived = true

          if (onSessionReceived) {
            onSessionReceived(sessionID)
          }

          if (!completed) {
            completed = true
            resolve(sessionID)
          }

          ws.close()
        }
      } catch (e) {
        console.warn("wsPreInit Failed to parse JSON:", event.data)
      }
    }
  })
}
